import { Dimension } from "../../../../models";
import DimensionDAO from "../DimensionDAO";
import ClientLogger from "../../../logging/ClientLogger";
import { ListResponse } from "../../../util/data/list/ListResponse";
import LoggerCommonTags from "../../../logging/LoggerCommonTags";
import ResourceNotFoundError from "../../../ResourceNotFoundError";
import Envelope from "../../../envelope/Envelope";
import DimensionV2 from "../../DimensionV2";
import DimensionV2Factory from "../../DimensionV2Factory";
import EnvelopeFactory from "../../../envelope/EnvelopeFactory";
import { MeasurementUnit } from "../../../../models";
import _ from "lodash";

export default class MockDataStoreDimensionDAO implements DimensionDAO {
    private static readonly STORAGE_NAME = "mock-dimensions";
    private static DELETE_ATTEMPT_METRIC_NAME = "MockDataStoreDimensionDAO.Delete.Attempt"
    private static DELETE_FAILURE_METRIC_NAME = "MockDataStoreDimensionDAO.Delete.Failure"
    private static CREATE_ATTEMPT_METRIC_NAME = "MockDataStoreDimensionDAO.Create.Attempt"
    private static CREATE_FAILURE_METRIC_NAME = "MockDataStoreDimensionDAO.Create.Failure"
    private static UPDATE_ATTEMPT_METRIC_NAME = "MockDataStoreDimensionDAO.Update.Attempt"
    private static UPDATE_FAILURE_METRIC_NAME = "MockDataStoreDimensionDAO.Update.Failure"

    private logger: ClientLogger;

    constructor(
        logger: ClientLogger
    ) {
        this.logger = logger;
    }

    private loadMock(): Dimension[] {
        let mock = new Array<Dimension>();
        const mockStr = localStorage.getItem(MockDataStoreDimensionDAO.STORAGE_NAME);

        if (mockStr) {
            mock = JSON.parse(mockStr) as Dimension[];
        }

        return mock;
    }

    private saveMock(mock: Dimension[]): void {
        localStorage.setItem(MockDataStoreDimensionDAO.STORAGE_NAME, JSON.stringify(mock));
    }

    async getDimensionById(dimensionId: string): Promise<Dimension> {
        const mock = this.loadMock();
        const result = mock.find((dimension): boolean => {
            return dimension.id === dimensionId;
        });
        
        if (!result) {
            throw new ResourceNotFoundError("Dimension");
        }
        return result;
    }

    public async create(record: Dimension): Promise<Dimension> {
        this.logger.info(
            `Creating dimension with id: ${record.id}`,
            record,
            [MockDataStoreDimensionDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const dimensionToCreate = new Dimension({
                ...record,
                localLastUpdatedTime: Date.now()
            });
            
            const mock = this.loadMock();
            const exists = mock.find((value): boolean => {
                return value.id === dimensionToCreate.id;
            });

            if (exists) {
                throw Error("Dimension already exists");
            }

            mock.push(dimensionToCreate);
            this.saveMock(mock);

            return dimensionToCreate;
        } catch (error) {
            this.logger.error(
                `Failed to create dimension with id: ${record.id}`,
                error,
                [MockDataStoreDimensionDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new Error("Failed to create the dimension");
        }
    }

    public async createDimensionFromV2Envelope(
        envelope: Envelope,
        propertyId: string,
        parentId: string
    ): Promise<Envelope> {
        const dimensionEnvelopeBody: DimensionV2 = envelope.body;
        const dimensionArguments = {
            propertyId: propertyId,
            parentId: parentId,
            description: dimensionEnvelopeBody.description,
            length: dimensionEnvelopeBody.length,
            lengthUnit: this.getV1UnitFromV2Unit(dimensionEnvelopeBody.measurementUnit),
            width: dimensionEnvelopeBody.width,
            widthUnit: dimensionEnvelopeBody.width ? this.getV1UnitFromV2Unit(dimensionEnvelopeBody.measurementUnit) : null,
        }
        
        const createdDimension = await this.create(new Dimension(dimensionArguments));
        const dimensionWithUpdatedSchema: DimensionV2 = DimensionV2Factory.fromV1Dimension(createdDimension);
        const result = EnvelopeFactory.createFromExistingRecord(
            "Dimension",
            2,
            dimensionWithUpdatedSchema,
            createdDimension.id
        );
        return result;
    }

    public async getDimensionsByParentId(parentId: string): Promise<Dimension[]> {
        try {
            const mock = this.loadMock();
            const filteredDimensions = mock.filter((dimension): boolean => {
                return dimension.parentId === parentId;
            });
            
            return filteredDimensions;
        } catch (error) {
            this.logger.error(
                `Error occurred while retrieving Dimensions by Parent ID for: ${parentId}`,
                error,
                []
            );
            throw error;
        }
    }

    public async getDimensionsByPropertyId(
        propertyId: string,
        nextToken?: string
    ): Promise<ListResponse<Dimension>> {
        const mock = this.loadMock();
        const dimensions = mock.filter((dimension): boolean => {
            return dimension.propertyId === propertyId;
        });
        
        return { items: dimensions, nextToken: null };
    }

    public async updateDimension(
        dimensionId: string,
        dimension: Dimension
    ): Promise<Dimension> {
        this.logger.info(
            `Attempting to update dimension with id: ${dimensionId}`,
            {},
            [MockDataStoreDimensionDAO.UPDATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const existingDimension: Dimension = await this.getDimensionById(dimensionId);
            let result: Dimension = {
                ...existingDimension,
                measurement: dimension.measurement,
                description: dimension.description,
                propertyId: dimension.propertyId,
                localLastUpdatedTime: Date.now()
            };

            if (!_.isEqual(existingDimension, result)) {
                const mock = this.loadMock();
                const index = mock.findIndex((value): boolean => {
                    return value.id === result.id;
                });

                if (index >= 0) {
                    mock[index] = result;
                    this.saveMock(mock);
                }
            } else {
                result = existingDimension;
            }
            
            return result;
        } catch (error) {
            this.logger.error(
                `Failed to update dimension with id: ${dimensionId}`,
                error,
                [MockDataStoreDimensionDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new Error("Failed to update the dimension");
        }
    }

    public async updateDimensionByEnvelope(
        dimensionId: string,
        dimension: Envelope
    ): Promise<Envelope> {
        const body: DimensionV2 = dimension.body;

        const existingRecord = await this.getDimensionEnvelopeById(dimensionId);
        if (existingRecord === undefined) {
            this.logger.info(
                "Record does not exist in datastore and will not be updated: " + dimensionId,
                {},
                []
            );
            return dimension;
        }
        if (_.isEqual(existingRecord.body, body)) {
            this.logger.info(
                `No changes detected to dimension ${dimensionId}, skipping update`,
                {},
                []
            );
            return dimension;
        }

        try {
            const existingDimension = await this.getDimensionById(dimensionId);
            const updatedDimension = {
                ...existingDimension,
                length: body.length,
                lengthUnit: this.getV1UnitFromV2Unit(body.measurementUnit),
                width: body.width,
                widthUnit: body.width ? this.getV1UnitFromV2Unit(body.measurementUnit) : undefined,
                description: body.description,
                localLastUpdatedTime: Date.now()
            };

            const result = await this.updateDimension(dimensionId, updatedDimension);
            
            const dimensionWithUpdatedSchema: DimensionV2 = DimensionV2Factory.fromV1Dimension(result);
            return EnvelopeFactory.update(
                dimension,
                dimension.header.dataType,
                dimension.header.version,
                dimensionWithUpdatedSchema
            );
        } catch (error) {
            throw error;
        }
    }

    public async deleteDimension(dimensionId: string): Promise<void> {
        this.logger.info(
            `Deleting dimension with id: ${dimensionId}`,
            {},
            [MockDataStoreDimensionDAO.DELETE_ATTEMPT_METRIC_NAME]
        );
        try {
            const mock = this.loadMock();
            const filteredMock = mock.filter((dimension): boolean => {
                return dimension.id !== dimensionId;
            });
            
            this.saveMock(filteredMock);
        } catch (error) {
            this.logger.error(
                `Failed to delete dimension with id: ${dimensionId}`,
                error,
                [MockDataStoreDimensionDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw error;
        }
    }

    public async getDimensionEnvelopeById(dimensionId: string): Promise<Envelope | undefined> {
        try {
            const dimension = await this.getDimensionById(dimensionId);
            const dimensionWithUpdatedSchema: DimensionV2 = DimensionV2Factory.fromV1Dimension(dimension);
            return EnvelopeFactory.createFromExistingRecord(
                "Dimension",
                2,
                dimensionWithUpdatedSchema,
                dimension.id
            );
        } catch (error) {
            return undefined;
        }
    }

    private getV1UnitFromV2Unit(unit: MeasurementUnit | undefined) {
        switch (unit) {
            case MeasurementUnit.CENTIMETER:
                return "centimeters";
            case MeasurementUnit.METER:
                return "meters"
            case MeasurementUnit.INCH:
                return "inches";
            case MeasurementUnit.FOOT:
                return "feet";
            default:
                return "feet";
        }
    }
} 